import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { 
  ArrowRight, 
  CheckCircle, 
  Heart,
  GraduationCap,
  Building2,
  Briefcase,
  Store,
  Rocket,
  TrendingUp,
  Target,
  Users,
  Award
} from 'lucide-react';

const industriesData = {
  'healthcare-aesthetics': {
    title: 'Healthcare & Aesthetics',
    subtitle: 'Digital marketing solutions for hospitals, clinics, and aesthetic practices',
    icon: Heart,
    heroImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200',
    description: 'We help healthcare providers and aesthetic clinics attract more patients through ethical, compliant digital marketing strategies.',
    challenges: [
      'Standing out in a competitive healthcare market',
      'Navigating advertising regulations and compliance',
      'Building trust and credibility online',
      'Converting website visitors into patient appointments'
    ],
    solutions: [
      'HIPAA-compliant marketing strategies',
      'Local SEO for geographic targeting',
      'Reputation management and review generation',
      'Patient education content marketing',
      'Google Ads campaigns for high-intent searches',
      'Social media presence building'
    ],
    results: [
      { value: '40%', label: 'Increase in Patient Inquiries' },
      { value: '#1', label: 'Local Search Rankings' },
      { value: '3x', label: 'Online Appointment Bookings' }
    ],
    testimonial: {
      quote: 'Digital Startup transformed our online presence. We\'ve seen a significant increase in patient inquiries since working with them.',
      author: 'Dr. Geetanjali Shetty',
      role: 'Founder, Aesthetic Clinic'
    },
    caseStudies: ['BTL Aesthetics India', 'Shreeji Eye Clinic', 'Medscape India']
  },
  'education-coaching': {
    title: 'Education & Coaching',
    subtitle: 'Drive enrollments and build thought leadership for educational institutions',
    icon: GraduationCap,
    heroImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200',
    description: 'We help schools, colleges, coaching centers, and educational startups increase enrollments and establish authority in their niche.',
    challenges: [
      'Seasonal enrollment cycles requiring consistent lead flow',
      'Competition from established institutions',
      'Demonstrating quality and outcomes to parents/students',
      'Managing reputation across multiple platforms'
    ],
    solutions: [
      'Strategic PPC campaigns for admission seasons',
      'SEO for academic program keywords',
      'Content marketing for thought leadership',
      'Social media community building',
      'Video marketing for campus and faculty showcases',
      'Review and rating management'
    ],
    results: [
      { value: '15-18%', label: 'Ad Conversion Rate' },
      { value: '#2', label: 'Ranking for "Best IGCSE School"' },
      { value: '3x', label: 'Enrollment Inquiries' }
    ],
    testimonial: {
      quote: 'Our admission inquiries tripled after partnering with Digital Startup. Their strategic approach to digital marketing is unmatched.',
      author: 'Priya Sharma',
      role: 'Principal, HVB Global Academy'
    },
    caseStudies: ['HVB Global Academy', 'Billabong High International School']
  },
  'real-estate': {
    title: 'Real Estate',
    subtitle: 'Generate quality leads and showcase properties effectively',
    icon: Building2,
    heroImage: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200',
    description: 'We help real estate developers, agents, and property managers generate quality leads and showcase properties to the right audience.',
    challenges: [
      'High competition for property-related keywords',
      'Long sales cycles requiring consistent nurturing',
      'Showcasing properties effectively online',
      'Generating qualified leads vs. tire-kickers'
    ],
    solutions: [
      'High-intent keyword targeting for PPC',
      'SEO for location-specific property searches',
      'Virtual tour and video marketing',
      'Landing page optimization for lead capture',
      'Retargeting campaigns for property viewers',
      'CRM integration for lead nurturing'
    ],
    results: [
      { value: '200+', label: 'Qualified Leads/Month' },
      { value: '50%', label: 'Reduction in Cost Per Lead' },
      { value: '5x', label: 'Website Traffic Growth' }
    ],
    testimonial: {
      quote: 'The quality of leads we receive has improved dramatically. Digital Startup understands real estate marketing.',
      author: 'Real Estate Client',
      role: 'Mumbai Developer'
    },
    caseStudies: ['Flexospaces.com', 'Pali Village Resort']
  },
  'professional-services': {
    title: 'Professional Services',
    subtitle: 'Position your expertise and attract high-value clients',
    icon: Briefcase,
    heroImage: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200',
    description: 'We help law firms, consulting agencies, accounting firms, and other professional service providers attract and convert high-value clients.',
    challenges: [
      'Differentiating in a trust-based industry',
      'Demonstrating expertise without giving away services',
      'Long client acquisition cycles',
      'Competing with established firms'
    ],
    solutions: [
      'Thought leadership content strategy',
      'LinkedIn marketing and advertising',
      'SEO for professional service keywords',
      'Case study and testimonial marketing',
      'Webinar and event marketing',
      'Email nurturing sequences'
    ],
    results: [
      { value: '150%', label: 'Increase in Qualified Leads' },
      { value: '4x', label: 'LinkedIn Engagement' },
      { value: '$50K+', label: 'Avg. Client Value' }
    ],
    testimonial: {
      quote: 'Digital Startup helped us position ourselves as industry leaders. The quality of inquiries has significantly improved.',
      author: 'Professional Services Client',
      role: 'Managing Partner'
    },
    caseStudies: ['Consulting Firm', 'Legal Practice']
  },
  'local-businesses': {
    title: 'Local Businesses',
    subtitle: 'Dominate local search and drive foot traffic',
    icon: Store,
    heroImage: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=1200',
    description: 'We help restaurants, retail stores, salons, and local service providers dominate local search and drive more customers through their doors.',
    challenges: [
      'Competing with nearby businesses',
      'Getting found in "near me" searches',
      'Managing online reviews and reputation',
      'Limited marketing budgets'
    ],
    solutions: [
      'Google Business Profile optimization',
      'Local SEO for map pack rankings',
      'Review generation and management',
      'Local PPC campaigns',
      'Social media marketing',
      'Community engagement strategies'
    ],
    results: [
      { value: '40%', label: 'Footfall Growth in 3 Months' },
      { value: '12', label: 'Keywords on Page 1' },
      { value: '4.8', label: 'Average Google Rating' }
    ],
    testimonial: {
      quote: 'Best decision we made for our business. Our footfall has increased dramatically since working with Digital Startup.',
      author: 'Rahul Mehta',
      role: 'MD, Pali Village Resort'
    },
    caseStudies: ['URBO Life', 'Margaret Chinese', 'Timeless Fine Dine']
  },
  'startups-smes': {
    title: 'Startups & SMEs',
    subtitle: 'Accelerate growth with data-driven marketing strategies',
    icon: Rocket,
    heroImage: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=1200',
    description: 'We help startups and small-to-medium businesses achieve rapid growth through strategic, cost-effective digital marketing.',
    challenges: [
      'Limited budgets requiring maximum ROI',
      'Building brand awareness from scratch',
      'Competing with larger, established players',
      'Scaling marketing as the business grows'
    ],
    solutions: [
      'Growth hacking strategies',
      'Performance marketing with strict ROI focus',
      'SEO for long-term organic growth',
      'Conversion rate optimization',
      'Marketing automation',
      'Scalable campaign structures'
    ],
    results: [
      { value: '4000%', label: 'Revenue Growth' },
      { value: '₹17L', label: 'Monthly Revenue' },
      { value: '300+', label: 'Keywords Ranked' }
    ],
    testimonial: {
      quote: 'Digital Startup helped us grow from ₹41K to ₹17 Lac monthly revenue in just 18 months. Incredible results.',
      author: 'E-commerce Client',
      role: 'Kashmirbox.com'
    },
    caseStudies: ['Kashmirbox.com', 'PrinteryDubai.com', 'The Watch Store']
  }
};

export const IndustryPage = ({ onOpenAuditModal }) => {
  const { slug } = useParams();
  const industry = industriesData[slug];
  usePageMeta(industry ? {
    title: `${industry.title} | Digital Startup`,
    description: industry.subtitle
  } : {});

  if (!industry) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-4">Industry Not Found</h1>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </main>
    );
  }

  const Icon = industry.icon;

  return (
    <main className="pt-20" data-testid={`industry-page-${slug}`}>
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-[#2E2E2E] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={industry.heroImage}
            alt={industry.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FFAA17] rounded-xl flex items-center justify-center">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17]">
                Industry Expertise
              </p>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4">
              {industry.title}
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              {industry.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={onOpenAuditModal}
                className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-6"
                data-testid="industry-cta-primary"
              >
                Get Free Growth Audit
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 rounded-full px-6"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
                Our Expertise
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-6">
                We Understand {industry.title}
              </h2>
              <p className="text-[#525252] mb-8 leading-relaxed">
                {industry.description}
              </p>
              <Button
                onClick={onOpenAuditModal}
                className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-6"
              >
                Get Industry-Specific Strategy <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div>
              <img
                src={industry.heroImage}
                alt={industry.title}
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-16 md:py-24 bg-[#F7F7F7]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Challenges */}
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
                Challenges
              </p>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">
                What You're Facing
              </h3>
              <ul className="space-y-4">
                {industry.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start gap-3 bg-white rounded-lg p-4">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 font-bold text-sm">{index + 1}</span>
                    </div>
                    <span className="text-[#525252]">{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Solutions */}
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
                Solutions
              </p>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">
                How We Help
              </h3>
              <ul className="space-y-4">
                {industry.solutions.map((solution, index) => (
                  <li key={index} className="flex items-start gap-3 bg-white rounded-lg p-4">
                    <CheckCircle className="w-6 h-6 text-[#FFAA17] flex-shrink-0" />
                    <span className="text-[#525252]">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 md:py-24 bg-[#2E2E2E]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Results
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              What We've Achieved for {industry.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {industry.results.map((result, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-[#FFAA17]">{result.value}</p>
                <p className="text-gray-400 mt-2">{result.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Award key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <blockquote className="text-xl md:text-2xl text-[#1A1A1A] italic mb-6">
              "{industry.testimonial.quote}"
            </blockquote>
            <p className="font-semibold text-[#1A1A1A]">{industry.testimonial.author}</p>
            <p className="text-[#525252]">{industry.testimonial.role}</p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 md:py-24 bg-[#F7F7F7]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Success Stories
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Clients We've Helped
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {industry.caseStudies.map((client, index) => (
              <div
                key={index}
                className="bg-white px-6 py-4 rounded-full shadow-sm"
              >
                <span className="text-[#1A1A1A] font-medium">{client}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button asChild className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-6">
              <Link to="/work">
                View All Case Studies <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#FFAA17]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Grow Your {industry.title} Business?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Get a free growth audit tailored specifically for your industry. 
            Discover opportunities your competitors are missing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onOpenAuditModal}
              className="bg-white text-[#FFAA17] hover:bg-gray-100 rounded-full px-8 py-3"
            >
              Get Free Growth Audit
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default IndustryPage;
